import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    SparklesIcon,
    LockClosedIcon,
    EyeIcon,
    EyeSlashIcon,
    ExclamationCircleIcon,
    CheckCircleIcon,
    ArrowRightIcon
} from '@heroicons/react/24/outline';

const PasswordReset = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { token } = useParams(); 
    const navigate = useNavigate();

    // Regex pour la validation du mot de passe
    const regexPassword = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;

    const validatePassword = (password) => {
        if (!regexPassword.test(password)) {
            setPasswordError('Le mot de passe doit comporter au moins 8 caractères, avec au moins une majuscule, une minuscule, un chiffre et un symbole.');
        } else {
            setPasswordError('');
        }
    };

    const validateConfirmPassword = (confirmPassword) => {
        if (confirmPassword !== password) {
            setConfirmPasswordError('Les mots de passe ne correspondent pas.');
        } else {
            setConfirmPasswordError('');
        }
    };

    const onChange = (e, field) => {
        const value = e.target.value;
        
        if (field === 'password') {
            setPassword(value);
            setPasswordError('');
        } else if (field === 'confirmPassword') {
            setConfirmPassword(value);
            setConfirmPasswordError('');
        }
        
        setMessage('');
        setError('');
    };

    const onBlur = (e, field) => {
        if (field === 'password' && e.target.value) {
            validatePassword(e.target.value);
        } else if (field === 'confirmPassword' && e.target.value) {
            validateConfirmPassword(e.target.value);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        validatePassword(password);
        validateConfirmPassword(confirmPassword);
        
        if (passwordError || confirmPasswordError) {
            return;
        }

        setIsSubmitting(true);
        setMessage('');
        setError('');

        try {
            const response = await fetch(`https://cvanalyzerback.osc-fr1.scalingo.io/api/password/reset-password/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ password, token }),
            });

            const data = await response.json();
            if (response.ok) {
                setMessage('Mot de passe mis à jour avec succès ! Redirection vers la page de connexion...');
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                setError(data.error || 'Une erreur est survenue lors de la réinitialisation.');
            }
        } catch (err) {
            setError('Erreur de connexion. Veuillez réessayer.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="page-shell flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center mb-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center mr-3">
                            <SparklesIcon className="w-6 h-6 text-white" />
                        </div>
                        <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                            Nouveau mot de passe
                        </h1>
                    </div>
                    <p className="text-gray-600">
                        Créez un nouveau mot de passe sécurisé pour votre compte
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Nouveau mot de passe */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
                            <LockClosedIcon className="w-4 h-4 mr-2 text-blue-600" />
                            Nouveau mot de passe
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => onChange(e, 'password')}
                                onBlur={(e) => onBlur(e, 'password')}
                                required
                                className={`w-full px-4 py-4 pr-12 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white ${
                                    passwordError ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="Créez un mot de passe sécurisé"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                {showPassword ? (
                                    <EyeSlashIcon className="w-5 h-5" />
                                ) : (
                                    <EyeIcon className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                        {passwordError && (
                            <div className="mt-2 flex items-start text-red-500 text-sm">
                                <ExclamationCircleIcon className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                                <span>{passwordError}</span>
                            </div>
                        )}
                    </div>

                    {/* Confirmation du mot de passe */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
                            <LockClosedIcon className="w-4 h-4 mr-2 text-blue-600" />
                            Confirmer le mot de passe
                        </label>
                        <div className="relative">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => onChange(e, 'confirmPassword')}
                                onBlur={(e) => onBlur(e, 'confirmPassword')}
                                required
                                className={`w-full px-4 py-4 pr-12 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white ${
                                    confirmPasswordError ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="Confirmez votre mot de passe"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                {showConfirmPassword ? (
                                    <EyeSlashIcon className="w-5 h-5" />
                                ) : (
                                    <EyeIcon className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                        {confirmPasswordError && (
                            <div className="mt-2 flex items-center text-red-500 text-sm">
                                <ExclamationCircleIcon className="w-4 h-4 mr-2 flex-shrink-0" />
                                <span>{confirmPasswordError}</span>
                            </div>
                        )}
                    </div>

                    {/* Bouton de soumission */}
                    <button
                        type="submit"
                        disabled={isSubmitting || passwordError || confirmPasswordError}
                        className={`w-full py-4 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center ${
                            isSubmitting || passwordError || confirmPasswordError
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1'
                        }`}
                    >
                        {isSubmitting ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                Mise à jour en cours...
                            </>
                        ) : (
                            <>
                                <ArrowRightIcon className="w-5 h-5 mr-2" />
                                Réinitialiser le mot de passe
                            </>
                        )}
                    </button>

                    {/* Messages */}
                    {message && (
                        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center">
                            <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                            <span className="text-green-600 text-sm">{message}</span>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center">
                            <ExclamationCircleIcon className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                            <span className="text-red-600 text-sm">{error}</span>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default PasswordReset;
